import { Directive, Optional } from '@angular/core';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { isWallClockLike, toLocalDate } from '../helpers/to-local-date';

/**
 * Hands every `ngxDaterangepickerMd` input a real `Date` instead of the
 * UTC-relabelled dayjs the library emits.
 *
 * The picker keeps its whole calendar in `dayjs(value).utc(true)` — the picked wall
 * clock with its zone rewritten to UTC — which is deliberate: date arithmetic on the
 * grid stays immune to DST that way. What it never does is unwrap that representation
 * on the way out. Applying a selection pushes the internal dayjs straight into the
 * bound control, so the instant the model receives is the wall clock *read as UTC* —
 * three hours early for Cairo in summer — and saving it persists a shifted date
 * (bug 27651). Nothing written *into* the picker is affected: `writeValue` passes the
 * caller's own object through untouched, which is why the value seeded from TypeScript
 * always looked right and only picking from the calendar broke it.
 *
 * The library exposes no timezone or UTC option to turn this off — not in 6.0.4, and
 * not in 17.0.0 either, where `.utc(true)` is still hardcoded across 56 call sites —
 * so the conversion has to happen at the boundary. Wrapping the value accessor's
 * change callback is that boundary, and it covers `ngModel` and reactive forms alike
 * without every call site having to remember.
 */
@Directive({
  selector: 'input[ngxDaterangepickerMd]'
})
export class DaterangepickerLocalDateDirective {

  constructor(@Optional() picker?: DaterangepickerDirective) {
    // Optional so the directive stays inert rather than throwing if the attribute
    // ever appears without `NgxDaterangepickerMd` in the same module's scope.
    if (!picker) {
      return;
    }

    const registerOnChange = picker.registerOnChange.bind(picker);
    picker.registerOnChange = ((onChange: (value: unknown) => void) =>
      registerOnChange(((value: unknown) => onChange(toLocalDates(value))) as any)) as any;

    const writeValue = picker.writeValue.bind(picker);
    picker.writeValue = ((value: unknown) => writeValue(toLocalDates(value) as any)) as any;
  }
}

/**
 * Rebuild a range with every date-ish member turned into a local `Date`.
 *
 * Used in both directions. Outbound it unwraps the picker's UTC-relabelled dayjs.
 * Inbound it rescues strings: the picker parses those with `locale.format`, and the
 * `'YYYY-MM-DDTHH:mm:ss.SSSSZ'` every one of these inputs declares parses *no* real
 * wire value — `SSSS` is not a dayjs token, so it reads `SSS` and then demands a
 * literal "S" — leaving the field showing "Invalid Date". Nothing hits that today
 * because every call site seeds from a model whose `clone()` coerces to `Date`, but
 * handing the picker one raw API row would be enough, so the guarantee is made here
 * rather than left to convention.
 *
 * Every key is walked rather than just `startDate`/`endDate`, because the directive
 * lets both be renamed through `[startKey]`/`[endKey]`. A value that is already a
 * `Date` is passed through by reference — the production order dialogs decide whether
 * to send `ignoreClientDates` by comparing the seeded object with `===` — and so is
 * anything else, including the `null`s the picker emits when it is cleared. The source
 * object belongs to its caller, so it is copied rather than written to.
 */
function toLocalDates(value: unknown): unknown {
  if (!value || typeof value !== 'object') {
    return value;
  }

  const source = value as Record<string, unknown>;
  let result = source;

  for (const key of Object.keys(source)) {
    if (!needsConverting(source[key])) {
      continue;
    }
    if (result === source) {
      result = { ...source };
    }
    result[key] = toLocalDate(source[key]);
  }

  return result;
}

/** A `Date` is already what we want; a string, number or wall clock is not. */
function needsConverting(value: unknown): boolean {
  return !(value instanceof Date)
    && (isWallClockLike(value) || typeof value === 'string' || typeof value === 'number');
}
